/**
 * Enquire Popup Tabs & Variant Update Handler
 * 
 * Uses event delegation to handle all enquire popups on the page.
 * This script should be loaded once and handles all popups dynamically.
 */
(function() {
  'use strict';

  // Cache for parsed variant data per product
  const variantCache = new Map();

  // Latest paint calculator estimate per product id
  const calculatorState = new Map();

  /**
   * Initialize tab functionality for a popup
   * @param {HTMLElement} popup - The popup element
   */
  function initPopupTabs(popup) {
    if (!popup || popup.dataset.tabsInitialized) return;
    popup.dataset.tabsInitialized = 'true';

    const initialProductId = popup.getAttribute('data-product-id');
    if (
      initialProductId &&
      !popup.classList.contains('enquire-popup--inline') &&
      calculatorState.has(String(initialProductId))
    ) {
      renderCalculatorSummary(popup, calculatorState.get(String(initialProductId)));
    }

    const tabButtons = popup.querySelectorAll('.enquire-popup__contact-item[data-tab]');
    const tabContents = popup.querySelectorAll('.enquire-popup__tab-content[data-tab-content]');

    tabButtons.forEach(function(button) {
      button.addEventListener('click', function() {
        const tabName = this.getAttribute('data-tab');

        // Remove active class from all buttons
        tabButtons.forEach(function(btn) {
          btn.classList.remove('enquire-popup__contact-item--active');
        });

        // Add active class to clicked button
        this.classList.add('enquire-popup__contact-item--active');

        // Hide all tab contents
        tabContents.forEach(function(content) {
          content.classList.remove('enquire-popup__tab-content--active');
        });

        // Show selected tab content
        const selectedContent = popup.querySelector('.enquire-popup__tab-content[data-tab-content="' + tabName + '"]');
        if (selectedContent) {
          selectedContent.classList.add('enquire-popup__tab-content--active');
        }
      });
    });
  }

  /**
   * Get cached variants for a product
   * @param {string} productId - The product ID
   * @returns {Array|null} - Array of variants or null
   */
  function getCachedVariants(productId) {
    if (variantCache.has(productId)) {
      return variantCache.get(productId);
    }

    const variantSelects = document.querySelector('variant-selects[data-product="' + productId + '"]');
    if (!variantSelects) return null;

    const variantsScript = variantSelects.querySelector('script[type="application/json"]');
    if (!variantsScript) return null;

    try {
      const variants = JSON.parse(variantsScript.textContent);
      variantCache.set(productId, variants);
      return variants;
    } catch (e) {
      console.error('Error parsing variants JSON:', e);
      return null;
    }
  }

  /**
   * Update product items in a popup with variant data
   * @param {HTMLElement} popup - The popup element
   * @param {Object} variant - The variant object
   * @param {string} currencySymbol - Currency symbol for price display
   */
  function updatePopupVariant(popup, variant, currencySymbol) {
    if (!popup || !variant) return;

    const productItems = popup.querySelectorAll('[data-enquire-popup-product-item]');
    if (!productItems.length) return;

    productItems.forEach(function(productItem) {
      // Update image
      const image = productItem.querySelector('[data-enquire-popup-product-image]');
      if (image && variant.featured_image) {
        const imageUrl = variant.featured_image.src || variant.featured_image;
        if (imageUrl) {
          image.src = imageUrl;
          image.srcset = variant.featured_image.srcset || '';
        }
      }

      // Update variant options
      const optionValues = productItem.querySelectorAll('[data-enquire-popup-option-value]');
      optionValues.forEach(function(span) {
        const optionPosition = parseInt(span.getAttribute('data-enquire-popup-option-value'), 10);
        const optionKey = 'option' + optionPosition;
        if (variant[optionKey]) {
          span.textContent = variant[optionKey];
        }
      });

      // Update price
      const priceElement = productItem.querySelector('[data-enquire-popup-product-price]');
      if (priceElement && variant.price) {
        const price = (variant.price / 100).toFixed(2);
        let priceText = currencySymbol + price;
        
        if (variant.unit_price_measurement && variant.unit_price_measurement.reference_value) {
          priceText += ' (' + variant.unit_price_measurement.reference_value + variant.unit_price_measurement.reference_unit + ')';
        }
        
        priceElement.textContent = priceText;
      }
    });
  }

  /**
   * Initialize all enquire popups on the page
   */
  function initAllPopups() {
    document.querySelectorAll('.enquire-popup').forEach(initPopupTabs);
  }

  /**
   * Handle variant change events
   * @param {CustomEvent} event - The variant change event
   */
  function handleVariantChange(event) {
    if (!event.detail || !event.detail.variant) return;

    const variant = event.detail.variant;
    const productId = event.detail.productId;
    
    // Find all popups with matching product data
    document.querySelectorAll('.enquire-popup[data-product-id="' + productId + '"]').forEach(function(popup) {
      const currencySymbol = popup.dataset.currencySymbol || '£';
      updatePopupVariant(popup, variant, currencySymbol);
    });

    // Also update any popup that's currently open (fallback)
    document.querySelectorAll('.enquire-popup.open').forEach(function(popup) {
      const currencySymbol = popup.dataset.currencySymbol || '£';
      const popupProductId = popup.dataset.productId;
      
      if (popupProductId) {
        const variants = getCachedVariants(popupProductId);
        if (variants) {
          const selectedVariant = variants.find(function(v) {
            return String(v.id) === String(variant.id);
          });
          if (selectedVariant) {
            updatePopupVariant(popup, selectedVariant, currencySymbol);
          }
        }
      }
    });
  }

  /**
   * Handle form changes for variant updates
   * @param {Event} event - The change event
   */
  function handleFormChange(event) {
    if (event.target.name !== 'id') return;

    const form = event.target.closest('form[data-type="add-to-cart-form"]');
    if (!form) return;

    // Use getAttribute to avoid collision with input[name="id"]
    const formId = form.getAttribute('id');
    if (!formId) return;

    const variantId = event.target.value;
    const productIdMatch = formId.match(/form-product-(?:main-product-)?(\d+)$/) || formId.match(/form-product-.*-(\d+)$/);
    if (!productIdMatch) return;

    const productId = productIdMatch[1];
    const variants = getCachedVariants(productId);
    if (!variants) return;

    const variant = variants.find(function(v) {
      return String(v.id) === String(variantId);
    });

    if (variant) {
      // Dispatch a synthetic variant:change event
      document.dispatchEvent(new CustomEvent('variant:change', {
        detail: { variant: variant, productId: productId }
      }));
    }
  }

  /**
   * Render the paint calculator estimate inside a popup and mirror it into the
   * hidden form field so it is submitted with the enquiry.
   * @param {HTMLElement} popup - The popup element
   * @param {Object} state - The latest calculator state (from paint-calculator:change)
   */
  function renderCalculatorSummary(popup, state) {
    const summaryEl = popup.querySelector('[data-enquire-calculator-summary]');
    const listEl = popup.querySelector('[data-enquire-calculator-summary-list]');
    const inputEl = popup.querySelector('[data-enquire-calculator-input]');
    const titleEl = popup.querySelector('[data-enquire-calculator-summary-title]');
    if (!summaryEl || !listEl || !inputEl) return;

    const lines = state && state.active && Array.isArray(state.lines) ? state.lines : [];

    if (!lines.length) {
      summaryEl.hidden = true;
      listEl.innerHTML = '';
      inputEl.value = '';
      return;
    }

    if (titleEl && state.title) titleEl.textContent = state.title;

    listEl.innerHTML = lines.map(function(line) {
      const note = line.note ? ' <span class="enquire-popup__calculator-note">(' + line.note + ')</span>' : '';
      return '<li>' +
        '<span class="enquire-popup__calculator-label">' + line.label + '</span>' +
        '<span class="enquire-popup__calculator-value">' + line.value + note + '</span>' +
        '</li>';
    }).join('');

    const textLines = lines.map(function(line) {
      return line.label + ': ' + line.value + (line.note ? ' (' + line.note + ')' : '');
    });
    inputEl.value = (state.title ? state.title + '\n' : '') + textLines.join('\n');

    summaryEl.hidden = false;
  }

  /**
   * Handle paint calculator updates and push them into matching popups
   * @param {CustomEvent} event - The paint-calculator:change event
   */
  function handleCalculatorChange(event) {
    if (!event.detail || event.detail.productId == null) return;

    const productId = String(event.detail.productId);
    calculatorState.set(productId, event.detail);

    // Product modal only — skip the contact-page inline embed (no estimate UI)
    document.querySelectorAll('.enquire-popup[data-product-id="' + productId + '"]:not(.enquire-popup--inline)').forEach(function(popup) {
      renderCalculatorSummary(popup, event.detail);
    });
  }

  // Initialize on DOM ready
  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', initAllPopups);
  } else {
    initAllPopups();
  }

  // Event delegation for popup trigger clicks
  document.addEventListener('click', function(e) {
    const trigger = e.target.closest('[data-enquire-popup-id]');
    if (trigger) {
      // Initialize tabs after a short delay to ensure popup is rendered
      setTimeout(function() {
        const popupId = trigger.getAttribute('data-enquire-popup-id');
        const popup = document.getElementById(popupId);
        if (popup) {
          initPopupTabs(popup);
        }
      }, 100);
    }
  });

  // Listen for variant change events
  document.addEventListener('variant:change', handleVariantChange);

  // Listen for paint calculator estimate updates
  document.addEventListener('paint-calculator:change', handleCalculatorChange);

  // Event delegation for form changes
  document.addEventListener('change', handleFormChange);

  // Re-initialize when new content is added (for dynamic content)
  if (typeof MutationObserver !== 'undefined') {
    const observer = new MutationObserver(function(mutations) {
      mutations.forEach(function(mutation) {
        mutation.addedNodes.forEach(function(node) {
          if (node.nodeType === Node.ELEMENT_NODE) {
            if (node.classList && node.classList.contains('enquire-popup')) {
              initPopupTabs(node);
            }
            const nestedPopups = node.querySelectorAll ? node.querySelectorAll('.enquire-popup') : [];
            nestedPopups.forEach(initPopupTabs);
          }
        });
      });
    });

    observer.observe(document.body, { childList: true, subtree: true });
  }
})();
